

import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  FaUsers, FaServicestack, FaBlog, FaSignOutAlt,
  FaEye, FaWhatsapp, FaChartLine,
  FaTachometerAlt, FaCalendarAlt, FaUserPlus, FaBell,
  FaPlus, FaShieldAlt, FaDatabase, FaCheckCircle
} from 'react-icons/fa';
import { AppContext } from '../../App';
import ServicesManager from '../../components/Admin/ServicesManager';
import BlogsManager from '../../components/Admin/BlogsManager';
import LeadsManager from '../../components/Admin/LeadsManager';

const AdminDashboard = () => {
  const { isAdminLoggedIn, handleAdminLogout, services, blogs, leads } = useContext(AppContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalServices: 0,
    activeServices: 0,
    totalBlogs: 0,
    publishedBlogs: 0,
    totalLeads: 0,
    todayLeads: 0,
    weekLeads: 0,
    monthLeads: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!isAdminLoggedIn) {
      navigate('/admin');
      return;
    }

    const today = new Date().toDateString();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const todayLeads = leads.filter(lead =>
      new Date(lead.timestamp).toDateString() === today
    ).length;

    const weekLeads = leads.filter(lead =>
      new Date(lead.timestamp) > weekAgo
    ).length;

    const monthLeads = leads.filter(lead =>
      new Date(lead.timestamp) > monthAgo
    ).length;

    setStats({
      totalServices: services.length,
      activeServices: services.filter(s => s.isActive).length,
      totalBlogs: blogs.length,
      publishedBlogs: blogs.filter(b => b.isPublished).length,
      totalLeads: leads.length,
      todayLeads: todayLeads,
      weekLeads: weekLeads,
      monthLeads: monthLeads
    });

    const newNotifications = [];
    if (todayLeads > 0) {
      newNotifications.push({
        id: Date.now(),
        title: 'New Leads Today',
        message: `${todayLeads} new lead${todayLeads > 1 ? 's' : ''} generated today`,
        time: 'Just now',
        type: 'success'
      });
    }
    if (services.length < 3) {
      newNotifications.push({
        id: Date.now() + 1,
        title: 'Add More Services',
        message: 'You have only a few services. Add more to attract customers.',
        time: 'Today',
        type: 'warning'
      });
    }
    setNotifications(newNotifications);
  }, [isAdminLoggedIn, navigate, services, blogs, leads]);

  const handleLogout = () => {
    handleAdminLogout();
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const tabs = [
    { id: 'overview', name: 'Dashboard Overview', icon: <FaTachometerAlt />, color: 'primary' },
    { id: 'services', name: 'Manage Services', icon: <FaServicestack />, color: 'blue' },
    { id: 'blogs', name: 'Manage Blogs', icon: <FaBlog />, color: 'green' },
    { id: 'leads', name: 'View Leads', icon: <FaUsers />, color: 'purple' },
  ];

  const statCards = [
    {
      title: 'Total Services',
      value: stats.totalServices,
      subValue: `${stats.activeServices} Active`,
      icon: <FaServicestack />,
      color: 'from-blue-500 to-blue-600',
      link: 'services'
    },
    {
      title: 'Total Blogs',
      value: stats.totalBlogs,
      subValue: `${stats.publishedBlogs} Published`,
      icon: <FaBlog />,
      color: 'from-green-500 to-green-600',
      link: 'blogs'
    },
    {
      title: 'Total Leads',
      value: stats.totalLeads,
      subValue: `${stats.todayLeads} Today`,
      icon: <FaUsers />,
      color: 'from-purple-500 to-purple-600',
      link: 'leads'
    },
    {
      title: 'Conversion Rate',
      value: `${stats.totalLeads > 0 ? Math.min(100, Math.round((stats.totalLeads / 100) * 10)) : 0}%`,
      subValue: 'Last 30 days',
      icon: <FaChartLine />,
      color: 'from-orange-500 to-orange-600',
      link: '#'
    },
  ];

  const recentLeads = leads.slice(0, 10);

  const getLeadIcon = (calculatorType) => {
    const icons = {
      'SIP': '📈',
      'Lumpsum': '💰',
      'SWP': '💵',
      'Retirement': '👴',
      'Child Education': '📚',
      'Marriage': '💒',
      'Home Loan': '🏠',
      'Step-Up SIP': '📊',
      'Combo': '🎯'
    };
    return icons[calculatorType] || '📋';
  };

  if (!isAdminLoggedIn) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white sticky top-0 z-50 shadow-lg">
        <div className="container-custom py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md">
                <span className="text-primary font-bold text-xl">WC</span>
              </div>
              <div>
                <h1 className="font-bold text-xl">Admin Dashboard</h1>
                <p className="text-xs opacity-80">Wisdom Consultancy</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaBell size={18} />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl overflow-hidden z-50"
                    >
                      <div className="bg-gray-50 px-4 py-2 border-b">
                        <h4 className="font-semibold text-gray-800">Notifications</h4>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.map(notif => (
                            <div key={notif.id} className="px-4 py-3 border-b hover:bg-gray-50">
                              <p className="font-semibold text-sm text-gray-800">{notif.title}</p>
                              <p className="text-xs text-gray-600">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center text-gray-500 text-sm">
                            No new notifications
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/" className="hover:bg-white/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm">
                <FaEye /> View Site
              </Link>

              <button
                onClick={handleLogout}
                className="hover:bg-white/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm"
              >
                <FaSignOutAlt /> Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white shadow-md sticky top-[73px] z-40">
        <div className="container-custom">
          <div className="flex overflow-x-auto hide-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id
                  ? `text-${tab.color}-600 border-b-2 border-${tab.color}-600`
                  : 'text-gray-600 hover:text-primary'
                  }`}
              >
                {tab.icon}
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="container-custom py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Welcome Banner */}
              <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-6 text-white">
                <h2 className="text-2xl font-bold mb-2">Welcome back, Admin! 👋</h2>
                <p className="opacity-90">Here's what's happening with your website today.</p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((card, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    onClick={() => card.link !== '#' && setActiveTab(card.link)}
                    className={`bg-gradient-to-r ${card.color} rounded-xl p-6 text-white shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-300`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm opacity-90">{card.title}</p>
                        <p className="text-3xl font-bold mt-1">{card.value}</p>
                        <p className="text-xs opacity-80 mt-2">{card.subValue}</p>
                      </div>
                      <div className="text-3xl opacity-50">{card.icon}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Recent Leads Table */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                  <h3 className="font-bold text-lg text-primary flex items-center gap-2">
                    <FaUserPlus /> Recent Leads
                  </h3>
                  <button
                    onClick={() => setActiveTab('leads')}
                    className="text-sm text-primary hover:underline"
                  >
                    View All →
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calculator</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {recentLeads.map((lead, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{lead.name}</div>
                            {lead.email && <div className="text-xs text-gray-500">{lead.email}</div>}
                          </td>
                          <td className="px-6 py-4">
                            <a href={`tel:${lead.phone}`} className="text-primary hover:underline">
                              {lead.phone}
                            </a>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              <span>{getLeadIcon(lead.calculatorType)}</span>
                              {lead.calculatorType}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <FaCalendarAlt size={12} />
                              {new Date(lead.timestamp).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <a
                                href={`https://wa.me/${lead.phone}?text=Hello ${lead.name}, thank you for your interest in Wisdom Consultancy. How can we help you?`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                              >
                                <FaWhatsapp className="text-green-600" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {recentLeads.length === 0 && (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                            No leads yet. Leads will appear here when users download calculator reports.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Quick Actions & System Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-primary mb-4 flex items-center gap-2">
                    <FaPlus /> Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('services')}
                      className="w-full text-left px-4 py-3 bg-white rounded-lg hover:shadow-md transition-all flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200">
                        <FaServicestack className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Add New Service</p>
                        <p className="text-xs text-gray-500">Create a new financial service offering</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('blogs')}
                      className="w-full text-left px-4 py-3 bg-white rounded-lg hover:shadow-md transition-all flex items-center gap-3 group"
                    >
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200">
                        <FaBlog className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">Write New Blog</p>
                        <p className="text-xs text-gray-500">Share financial insights with your audience</p>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-primary mb-4 flex items-center gap-2">
                    <FaDatabase /> System Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">System Status</span>
                      <span className="text-sm font-semibold text-green-600 flex items-center gap-1">
                        <FaCheckCircle /> All Systems Operational
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="text-gray-600">Support Contact</span>
                      <span className="text-sm font-semibold">9726344477</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info Card */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-lg text-primary mb-4 flex items-center gap-2">
                  <FaShieldAlt /> Business Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">📍 Address</p>
                    <p className="font-medium">E-44, Vasant Vihar Society, GIDC -Vadsar Road, Vadodara-390010</p>
                  </div>
                  <div>
                    <p className="text-gray-500">📞 Phone Numbers</p>
                    <p className="font-medium">9726344477, 9726766777</p>
                  </div>
                  <div>
                    <p className="text-gray-500">✉️ Email</p>
                    <p className="font-medium">Wisdom2secure@gmail.com</p>
                  </div>
                  <div>
                    <p className="text-gray-500">⏰ Working Hours</p>
                    <p className="font-medium">Mon-Sat: 10:00 AM - 7:00 PM</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'services' && <ServicesManager />}
          {activeTab === 'blogs' && <BlogsManager />}
          {activeTab === 'leads' && <LeadsManager />}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDashboard;